import { t } from '../../../i18n';
import { Header } from './Header';

/**
 * 状态类型：loading（加载中）、empty（无数据）、error（加载失败）
 */
type StateType = 'loading' | 'empty' | 'error';

/**
 * PageState 组件属性
 */
interface PageStateProps {
    /**
     * 是否显示页面头部（Header 组件）
     */
    withHeader?: boolean;
    /**
     * 页面头部标题，仅在 withHeader 为 true 时生效
     */
    headerTitle?: string;
    /**
     * 是否撑满全屏，默认 true
     */
    fullHeight?: boolean;
    /**
     * 显示状态：loading（默认）、empty（无数据）、error（加载失败）
     */
    state?: StateType;
    /**
     * 加载失败时的重试回调函数，仅在 state 为 'error' 时生效
     */
    onRetry?: () => void;
}

/**
 * PageState 组件
 *
 * 用于展示加载中、无数据、加载失败三种状态的通用组件
 * 支持带头部和无头部两种模式
 *
 * @example
 * // 显示加载状态（带头部）
 * <PageState withHeader headerTitle="传入" />
 *
 * // 显示无数据状态
 * <PageState state="empty" />
 *
 * // 显示加载失败状态（带重试按钮）
 * <PageState state="error" onRetry={() => refetch()} />
 */
export const PageState = ({ withHeader, headerTitle, fullHeight = true, state = 'loading', onRetry }: PageStateProps) => {
    const containerClass = fullHeight ? 'w-full h-screen' : 'w-full';
    const containerStyle = fullHeight ? 'flex flex-col' : '';

    /**
     * 根据状态返回对应的消息文案
     */
    const getMessage = () => {
        if (state === 'empty') {
            return t('pda.common.noData') || 'No Data';
        }
        if (state === 'error') {
            return t('pda.common.loadFailed') || 'Load Failed';
        }
        return t('pda.common.loading') || 'Loading...';
    };

    /**
     * 无数据和错误状态下应用灰色文字样式
     */
    const textClass = state === 'empty' || state === 'error' ? 'text-gray-500' : '';

    /**
     * 内容区域，包含消息和（可选的）重试按钮
     */
    const content = (
        <div className={`text-center ${textClass}`}>
            <div>{getMessage()}</div>
            {state === 'error' && onRetry && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRetry();
                    }}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 active:bg-purple-800 cursor-pointer"
                >
                    {t('pda.common.retry') || 'Retry'}
                </button>
            )}
        </div>
    );

    // 带头部的模式：显示 Header 和内容
    if (withHeader) {
        return (
            <div className={`${containerClass} ${containerStyle} pt-2`}>
                <Header title={headerTitle || ''} />
                <div className="flex-1 bg-gray-50 flex items-center justify-center">
                    {content}
                </div>
            </div>
        );
    }

    // 无头部的模式：仅显示内容
    return (
        <div className={`${containerClass} bg-gray-50 flex items-center justify-center`}>
            {content}
        </div>
    );
};
